import type React from 'react'
import type { JSX, Ref } from 'react'
import type { ActionButtonColors } from 'atoms/ActionButton/ActionButton.h'
import type { IconNames } from 'atoms/Icon/Icon.h'
import type { COLOR, FIXED_INDENTS, INDENTS } from 'vars/index'
import type {
  BUTTON_PADDINGS,
  ButtonBorderColors,
  ButtonBordersWidth,
  ButtonColors,
  ButtonFluid,
  ButtonIconSizes,
  ButtonLoaderSizes,
} from './Button.h'

import { Icon, IconSizes } from 'atoms/Icon'
import { TextBox, TextBoxTypes } from 'atoms/TextBox'
import {
  ButtonCorners,
  ButtonFonts,
  ButtonLoaderSizes as LoaderSizes,
  IconPositions,
} from './Button.h'
import {
  ButtonCenteredLoaderSC,
  ButtonContentSC,
  ButtonHiddenContentSC,
  ButtonLoaderSlotSC,
  ButtonOverlaySC,
  ButtonSC,
} from './Button.styled'
import { ButtonLoader } from './ButtonLoader'

export interface IButtonProps {
  ref?: Ref<HTMLButtonElement>
  type?: 'button' | 'submit' | 'reset'
  name?: string
  value?: string
  content?: boolean | string
  font?: ButtonFonts | TextBoxTypes
  title?: string
  icon?: IconNames
  iconPosition?: IconPositions
  iconOrder?: 1 | 2 | 3
  iconSize?: ButtonIconSizes
  iconHeight?: ButtonIconSizes
  colorDefault?: ButtonColors | ActionButtonColors
  colorHovered?: ButtonColors | ActionButtonColors
  colorPressed?: ButtonColors | ActionButtonColors
  colorDisabled?: ButtonColors | ActionButtonColors
  backgroundColorDefault?: COLOR
  backgroundColorHovered?: COLOR
  backgroundColorPressed?: COLOR
  backgroundColorDisabled?: COLOR
  gradient?: COLOR
  paddings?: INDENTS[] | FIXED_INDENTS[] | BUTTON_PADDINGS[]
  corners?: ButtonCorners
  borderWidth?: ButtonBordersWidth
  bordersColorDefault?: ButtonBorderColors
  bordersColorHovered?: ButtonBorderColors
  bordersColorPressed?: ButtonBorderColors
  bordersColorDisabled?: ButtonBorderColors
  fluid?: ButtonFluid
  className?: 'hovered' | 'pressed'
  disabled?: boolean
  onClick?: (event: React.MouseEvent) => void
  isLoading?: boolean
  withLoader?: boolean
  hideTitleOnLoading?: boolean
  loaderSize?: ButtonLoaderSizes
  tabIndex?: number
  dataTestId?: string
  ariaLabel?: string
  ariaExpanded?: boolean
}

export function Button({
  ref,
  type = 'button',
  name,
  // value,
  content = true,
  font = ButtonFonts.Button1,
  title,
  icon,
  iconPosition,
  iconSize,
  iconHeight,
  colorDefault,
  colorHovered,
  colorPressed,
  colorDisabled,
  backgroundColorDefault,
  backgroundColorHovered,
  backgroundColorPressed,
  backgroundColorDisabled,
  gradient,
  paddings,
  corners = ButtonCorners.CORNERS_10,
  borderWidth,
  bordersColorDefault,
  bordersColorHovered,
  bordersColorPressed,
  bordersColorDisabled,
  fluid,
  className,
  disabled,
  onClick,
  isLoading,
  withLoader = false,
  hideTitleOnLoading = false,
  loaderSize = LoaderSizes.small,
  tabIndex,
  dataTestId,
  ariaLabel,
  ariaExpanded,
}: IButtonProps): JSX.Element {
  if (!content) return <></>

  // prettier-ignore
  const iconOrder = iconPosition === IconPositions.Center ? 3
                  : iconPosition === IconPositions.Left   ? 1
                                                          : 2

  const hasTitle = Boolean(title)
  const hasLoader = Boolean(isLoading) && withLoader
  const showCenteredLoaderOnly = hasTitle && hasLoader && hideTitleOnLoading // prettier-ignore
  const showInlineLoaderLayout = hasTitle && !icon && withLoader && !hideTitleOnLoading // prettier-ignore
  const showStandaloneLoader = hasLoader && !showCenteredLoaderOnly && !showInlineLoaderLayout // prettier-ignore

  const renderLoader = (hasTitleOffset: boolean): JSX.Element => (
    <ButtonLoader loaderSize={loaderSize} hasTitle={hasTitleOffset} />
  )

  return (
    <ButtonSC
      data-arcana-button='true'
      ref={ref}
      type={type}
      name={name}
      disabled={disabled}
      paddings={paddings}
      gradient={gradient}
      corners={corners}
      borderWidth={borderWidth}
      bordersColorDefault={bordersColorDefault}
      bordersColorHovered={bordersColorHovered}
      bordersColorPressed={bordersColorPressed || bordersColorHovered}
      bordersColorDisabled={bordersColorDisabled}
      className={className!}
      fluid={fluid}
      iconOrder={iconOrder}
      colorDefault={colorDefault}
      colorHovered={colorHovered || colorDefault}
      colorPressed={colorPressed || colorDefault}
      colorDisabled={colorDisabled}
      backgroundColorDefault={backgroundColorDefault}
      backgroundColorHovered={backgroundColorHovered || backgroundColorDefault}
      backgroundColorPressed={backgroundColorPressed || backgroundColorDefault}
      backgroundColorDisabled={backgroundColorDisabled}
      font={TextBoxTypes[font]}
      onClick={onClick}
      isLoading={isLoading}
      tabIndex={tabIndex}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
    >
      <ButtonOverlaySC />

      {showStandaloneLoader && renderLoader(hasTitle)}

      {icon && (
        <Icon
          name={icon}
          size={iconSize ? IconSizes[iconSize] : null}
          height={iconHeight ? IconSizes[iconHeight] : null}
        />
      )}

      {showCenteredLoaderOnly ? (
        <>
          <ButtonHiddenContentSC aria-hidden='true'>
            <TextBox title={title!} maxLines={1} />
          </ButtonHiddenContentSC>
          <ButtonCenteredLoaderSC>{renderLoader(false)}</ButtonCenteredLoaderSC>
        </>
      ) : showInlineLoaderLayout ? (
        <ButtonContentSC loaderSize={loaderSize}>
          <ButtonLoaderSlotSC loaderSize={loaderSize}>
            {hasLoader && renderLoader(false)}
          </ButtonLoaderSlotSC>
          <TextBox title={title!} maxLines={1} />
        </ButtonContentSC>
      ) : (
        title && <TextBox title={title} maxLines={1} />
      )}
    </ButtonSC>
  )
}
